import React from 'react'
import { Box } from '@mui/material'
import Router from 'next/router'
import { useSession } from 'next-auth/react'

import { useShowDataStore } from '#/shared/lib/hooks/use-show-data'
import LightningSvg from '#/assets/svg/lightning.svg?react'
import { accountApi } from '#/shared/api/account-endpoints'
import { commaSeparated } from '#/shared/lib/helpers'
import { IFeatures, IOffer, formatFeatureValue } from '#/views/subscription'

import styles from './offer.module.scss'

interface IOfferProps extends IOffer {
	device: 'desktop' | 'mobile'
	isCurrentSubscription?: boolean
	index?: number
	currentPlanTokens?: number
	ctaLabel?: string
	onPayError?: () => void
}

const Offer: React.FC<IOfferProps> = ({
	uid,
	tokens_per_plan,
	price,
	grouped_features,
	isCurrentSubscription,
	index,
	currentPlanTokens = 0,
	ctaLabel,
	onPayError,
}) => {
	const { data } = useSession()
	const { showMessage } = useShowDataStore()

	const unitMap: Record<string, string> = {
		Изображения: 'изображений',
		'Чат-боты': 'страниц текста',
		Видео: 'видео',
		Аудио: 'аудиозаписей',
		Музыка: 'минут',
		Голос: 'минут',
	}

	const formatFeatures = (groupedFeatures: IFeatures[] | undefined): string[] => {
		if (!groupedFeatures || !Array.isArray(groupedFeatures)) {
			return []
		}

		return groupedFeatures.flatMap((group) => {
			if (!group?.features || !Array.isArray(group.features)) {
				return []
			}

			const limit = group.name === 'Изображения' ? 2 : 1
			const featuresToShow = group.features.slice(0, limit)

			return featuresToShow
				.filter((feature) => feature.quantity !== 0)
				.map((feature) => {
					const groupName = unitMap[group.name] || group.name
					const { number, more } = formatFeatureValue(feature.quantity, feature.measurement_unit)
					return `${more ? more + ' ' : ''}${number} ${groupName} в ${feature.name}`
				})
		})
	}

	const formattedFeatures = formatFeatures(grouped_features)

	const pay = async (planUid: string) => {
		try {
			const urlForPay = await accountApi.payProduct(data!.access, planUid)
			urlForPay ? await Router.push(urlForPay) : null
		} catch (err: any) {
			if (onPayError) {
				onPayError()
				return
			}
			const detail = err?.response?.data?.detail
			const message = detail ?? 'Произошла ошибка при оплате'
			showMessage(message)
		}
	}

	const handlePayClick = () => {
		pay(uid)
	}

	const buttonLabel =
		ctaLabel ?? (isCurrentSubscription ? 'Восстановить токены' : 'Оплатить')

	return (
		<Box className={`${styles.tarif} ${index === 1 ? styles.tarif_bg_1 : ''} ${index === 2 ? styles.tarif_bg_2 : ''}`}>
			<Box>
				<Box className={styles.tarif_header}>
					<Box className={styles.tarif_badge}>
						<LightningSvg color='#8280FF' width={16} height={24} />
						<span>{commaSeparated(Math.round(Number(tokens_per_plan)))} токенов</span>
					</Box>
					<Box className={styles.tarif_blur_area}></Box>
				</Box>

				<ul className={styles.tarif_list}>
					{formattedFeatures.map((feature, idx) => (
						<li key={idx} className={styles.point}>
							{feature}
						</li>
					))}
				</ul>
			</Box>
			<Box>
				<p className={styles.tarif_price}>{commaSeparated(parseInt(price) ?? 0)} ₽</p>
				<Box
					className={`${styles.tarif_btn} ${isCurrentSubscription ? styles.tarif_btn_current : ''}`}
					onClick={(e) => {
						e.stopPropagation()
						handlePayClick()
					}}
				>
					{buttonLabel}
				</Box>
			</Box>
		</Box>
	)
}

export default Offer
